import { styled } from '@linaria/react';
import { Button } from '@mantine/core';
import type { IntegrationHealthCheck } from '../../../../api/integration-health-client/integration-health-client.type';

import { skyDark } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DropShadowContainer } from '../../../../components/drop-shadow-container/drop-shadow-container';
import { getDisplayName } from '../data-management.helpers';
import type { TableColumn } from '../table';
import { Table } from '../table';
import { CHECK_TYPE_LABELS, type TableRow } from './integration-health-tab.type';
interface IntegrationHealthTabProps {
  issues: IntegrationHealthCheck[];
  onDismiss: (issueId: string) => void;
  isDismissing: boolean;
}

export function IntegrationHealthTab({ issues, onDismiss, isDismissing }: IntegrationHealthTabProps) {
  if (issues.length === 0) {
    return (
      <TabContent>
        <HealthCard>
          <EmptyState>
            <Icon name="check_circle" size={40} color={skyDark} />
            <Text size="large" color={skyDark}>
              No integration issues detected
            </Text>
          </EmptyState>
        </HealthCard>
      </TabContent>
    );
  }

  const tableData: TableRow[] = issues
    .slice()
    .sort((a, b) => {
      const labelA = CHECK_TYPE_LABELS[a.check_type] ?? a.check_type;
      const labelB = CHECK_TYPE_LABELS[b.check_type] ?? b.check_type;
      return labelA.localeCompare(labelB) || a.external_identifier.localeCompare(b.external_identifier);
    })
    .map((issue) => ({
      key: issue.id,
      id: issue.id,
      name: getDisplayName(issue),
      externalIdentifier: issue.external_identifier,
      checkType: CHECK_TYPE_LABELS[issue.check_type] ?? issue.check_type,
      resolutionUrl: issue.resolution_url,
    }));

  const columns: TableColumn<TableRow>[] = [
    { key: 'name', label: 'Name', width: '25%', sortable: true },
    { key: 'externalIdentifier', label: 'External ID', width: '20%', sortable: true },
    { key: 'checkType', label: 'Issue Type', width: '20%', sortable: true },
    {
      key: 'resolutionUrl',
      label: 'Resolution',
      width: '20%',
      sortable: false,
      render: (_value, row) =>
        row.resolutionUrl ? (
          <Button
            component="a"
            href={row.resolutionUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="outline"
            radius="xl"
            leftSection={<Icon name="settings" size={14} color={skyDark} />}
          >
            Go to Settings
          </Button>
        ) : (
          <Text size="small" color={skyDark}>
            Contact Admin
          </Text>
        ),
    },
    {
      key: 'id',
      label: '',
      width: '15%',
      sortable: false,
      render: (_value, row) => (
        <Button
          size="xs"
          variant="outline"
          radius="xl"
          color="gray"
          disabled={isDismissing}
          loading={isDismissing}
          onClick={() => onDismiss(row.id)}
        >
          Dismiss
        </Button>
      ),
    },
  ];

  return (
    <TabContent>
      <HealthCard>
        <TableContainer>
          <Table data={tableData} columns={columns} getRowId={(row) => row.key} stickyHeader />
        </TableContainer>
      </HealthCard>
    </TabContent>
  );
}

const TabContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  margin: 16px 0px 0px 0px;
`;

const HealthCard = styled(DropShadowContainer)`
  border-radius: 8px;
  flex: 1;
  padding: 24px 16px 16px 16px;
  height: calc(100vh - 172px);
  min-height: 0;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding-right: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 64px;
`;
